import React, { useState } from "react";
import "./InputGroup3RadioButtonsSet.css";

export interface RadioOption {
  /**
   * Valor de la opción
   */
  value: string;
}

export interface InputGroup3RadioButtonsSetProps {
  /**
   * Etiqueta del grupo
   */
  label: string;

  /**
   * Array de opciones (idealmente 3)
   */
  options: RadioOption[];

  /**
   * Valor seleccionado actualmente
   */
  value?: string;

  /**
   * Función a ejecutar cuando cambia el valor seleccionado
   */
  onChange?: (value: string) => void;

  /**
   * Si se muestra el ícono de ayuda
   */
  showHelpIcon?: boolean;

  /**
   * Si se muestra el ícono de advertencia
   */
  showWarningIcon?: boolean;

  /**
   * Etiqueta opcional que se muestra en un tag (ej: IF-VIG)
   */
  tagLabel?: string;

  /**
   * Si el grupo está deshabilitado
   */
  disabled?: boolean;

  /**
   * Si el grupo está en estado de error
   */
  error?: boolean;

  /**
   * Mensaje de error a mostrar
   */
  errorMessage?: string;

  /**
   * Si el grupo está en modo de vista (sólo lectura)
   */
  viewMode?: boolean;

  /**
   * Si el componente se muestra en versión móvil
   */
  isMobile?: boolean;

  /**
   * Clase CSS adicional
   */
  className?: string;
}

/**
 * Grupo de 3 radio buttons con etiqueta y estados
 */
export const InputGroup3RadioButtonsSet: React.FC<
  InputGroup3RadioButtonsSetProps
> = ({
  label,
  options,
  value,
  onChange,
  showHelpIcon = false,
  showWarningIcon = false,
  tagLabel,
  disabled = false,
  error = false,
  errorMessage,
  viewMode = false,
  isMobile = false,
  className = "",
}) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value);

  // Manejo de cambio de selección
  const handleChange = (optionValue: string) => {
    if (disabled) return;

    setSelectedValue(optionValue);
    if (onChange) {
      onChange(optionValue);
    }
  };

  // Determinar las clases CSS basadas en los props
  const containerClasses = [
    "qida-radio-group-3",
    `qida-radio-group-3--${isMobile ? "mobile" : "desktop"}`,
    disabled ? "qida-radio-group-3--disabled" : "",
    error ? "qida-radio-group-3--error" : "",
    viewMode ? "qida-radio-group-3--view-mode" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Asegurarnos de que siempre mostramos exactamente 3 opciones
  const displayOptions = options.slice(0, 3);
  if (displayOptions.length < 3) {
    // Rellenar con opciones vacías si hay menos de 3
    while (displayOptions.length < 3) {
      displayOptions.push({
        value: `empty-${displayOptions.length}`,
      });
    }
  }

  // Si estamos en modo vista, mostrar un formato diferente
  if (viewMode) {
    return (
      <div className={containerClasses}>
        <div className="qida-radio-group-3__view-header">
          <div className="qida-radio-group-3__label">{label}</div>
        </div>
        <div className="qida-radio-group-3__details">
          {selectedValue && "Lorem ipsum"}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      {/* Cabecera del grupo */}
      <div className="qida-radio-group-3__header">
        <div className="qida-radio-group-3__label">{label}</div>

        <div className="qida-radio-group-3__icons">
          {showHelpIcon && (
            <div className="qida-radio-group-3__icon qida-radio-group-3__icon--help">
              <svg
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.5 18.375C14.8492 18.375 18.375 14.8492 18.375 10.5C18.375 6.15076 14.8492 2.625 10.5 2.625C6.15076 2.625 2.625 6.15076 2.625 10.5C2.625 14.8492 6.15076 18.375 10.5 18.375Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.09375 8.09375C8.26233 7.6834 8.55765 7.33908 8.93688 7.10911C9.31611 6.87914 9.75958 6.77519 10.2018 6.81257C10.644 6.84996 11.0636 7.02695 11.396 7.31765C11.7283 7.60834 11.9554 7.9961 12.0443 8.42812C12.0443 9.1875 11.2499 9.5625 10.5 9.5625V10.9375"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.5 13.125C10.5 13.125 10.5044 13.125 10.5 13.125C10.3512 13.125 10.2087 13.1842 10.1038 13.2892C9.99888 13.3941 9.93962 13.5366 9.93962 13.6855C9.93962 13.8343 9.99888 13.9768 10.1038 14.0817C10.2087 14.1867 10.3512 14.2459 10.5 14.2459C10.6488 14.2459 10.7913 14.1867 10.8962 14.0817C11.0011 13.9768 11.0604 13.8343 11.0604 13.6855C11.0604 13.5366 11.0011 13.3941 10.8962 13.2892C10.7913 13.1842 10.6488 13.125 10.5 13.125"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}

          {showWarningIcon && (
            <div className="qida-radio-group-3__icon qida-radio-group-3__icon--warning">
              <svg
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.5 18.375C14.8492 18.375 18.375 14.8492 18.375 10.5C18.375 6.15076 14.8492 2.625 10.5 2.625C6.15076 2.625 2.625 6.15076 2.625 10.5C2.625 14.8492 6.15076 18.375 10.5 18.375Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.5 7V10.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.5 14H10.5087"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}

          {tagLabel && (
            <div className="qida-radio-group-3__tag">
              <span>{tagLabel}</span>
            </div>
          )}
        </div>
      </div>

      {/* Contenedor de opciones */}
      <div className="qida-radio-group-3__options-container">
        <div className="qida-radio-group-3__options">
          {displayOptions.map((option, index) => (
            <div
              key={option.value}
              className={`qida-radio-group-3__option ${selectedValue === option.value ? "qida-radio-group-3__option--selected" : ""}`}
              onClick={() => !disabled && handleChange(option.value)}
            >
              <div className="qida-radio-group-3__radio">
                <div className="qida-radio-group-3__radio-icon">
                  <div
                    className={`qida-radio-group-3__radio-circle ${selectedValue === option.value ? "qida-radio-group-3__radio-circle--selected" : ""}`}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mensaje de error */}
        {error && errorMessage && (
          <div className="qida-radio-group-3__error-message">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default InputGroup3RadioButtonsSet;
